// pages/Products.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  getProducts, 
  deactivateProduct,
  activateProduct 
} from '../services/productService';
import { getCategories } from '../services/categoryService';
import { getManufacturers } from '../services/manufacturerService';
import { getWeightUnitDisplayName, getVolumeUnitDisplayName } from '../services/unitsService';
import { getIcon } from '../utils/icons';
import { toast } from 'react-toastify';

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialPage = parseInt(new URLSearchParams(location.search).get('page') || '1', 10);

  // Состояния
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // Фильтры
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [manufacturerFilter, setManufacturerFilter] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showInactive, setShowInactive] = useState(true); // По умолчанию показываем неактивные

  // Загрузка товаров при монтировании компонента и изменении страницы
  useEffect(() => {
    fetchCategories();
    fetchManufacturers();
    fetchProducts(page, pageSize);
    // Обновляем URL с текущей страницей
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
  }, [page, pageSize]);

  const filtersInitialized = useRef(false);

  // Обработка изменения поисковых фильтров с задержкой
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(setTimeout(() => {
      setPage(1);
      fetchProducts(1, pageSize);
    }, 500));

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm, categoryFilter, manufacturerFilter, showInactive]);

  // Загрузка категорий
  const fetchCategories = async () => {
    try {
      const data = await getCategories({}, true);
      const categoriesData = Array.isArray(data) ? data : (data.items || data.data || []);
      setCategories(categoriesData.filter(c => c.isActive));
    } catch (error) {
      console.error('Ошибка загрузки категорий:', error);
    }
  };

  // Загрузка производителей
  const fetchManufacturers = async () => {
    try {
      const data = await getManufacturers({}, true);
      const manufacturersData = Array.isArray(data) ? data : (data.items || data.data || []);
      setManufacturers(manufacturersData.filter(m => m.isActive));
    } catch (error) {
      console.error('Ошибка загрузки производителей:', error);
    }
  };

  // Загрузка товаров с фильтрацией
  const fetchProducts = async (pageNum, pageSizeNum) => {
    try {
      setLoading(true);
      
      // Формируем параметры запроса
      const params = {
        page: pageNum,
        pageSize: pageSizeNum
      };
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      if (categoryFilter) {
        params.categoryId = parseInt(categoryFilter, 10);
      }
      
      if (manufacturerFilter) {
        params.manufacturerId = parseInt(manufacturerFilter, 10);
      }
      
      // Получаем данные с API
      const data = await getProducts(params, showInactive);
      
      // Обработка данных для пагинации
      if (data && typeof data === 'object' && data.items) {
        // Новый формат API с пагинацией
        setProducts(data.items || []);
        setTotalItems(data.totalCount || 0);
        setTotalPages(data.totalPages || 1);
      } else if (Array.isArray(data)) {
        // Старый формат API - простой массив (для обратной совместимости)
        setProducts(data);
        setTotalItems(data.length);
        setTotalPages(Math.max(1, Math.ceil(data.length / pageSizeNum)));
      } else {
        // Неизвестный формат
        console.error('Unexpected API response format:', data);
        setProducts([]);
        setTotalItems(0);
        setTotalPages(1);
      }
      
      setPage(pageNum);
      setPageSize(pageSizeNum);
    } catch (error) {
      console.error('Ошибка загрузки товаров:', error);
      toast.error('Не удалось загрузить товары');
    } finally {
      setLoading(false);
    }
  };

  // Переход на страницу создания нового товара
  const handleCreateProduct = () => {
    navigate('/products/new');
  };

  // Переход на страницу редактирования товара
  const handleEditProduct = (id) => {
    navigate(`/products/${id}/edit`);
  };

  // Просмотр подробной информации о товаре
  const handleViewProduct = (id) => {
    navigate(`/products/${id}`);
  };

  // Открытие диалога подтверждения удаления
  const openDeleteConfirm = (id) => {
    setConfirmDelete(id);
  };

  // Закрытие диалога подтверждения удаления
  const closeDeleteConfirm = () => {
    setConfirmDelete(null);
  };

  // Обработка деактивации товара
  const handleDeactivate = async (id) => {
    try {
      // Используем специальный метод для деактивации
      await deactivateProduct(id);
      fetchProducts(page, pageSize);
      toast.success('Товар успешно деактивирован');
      if (confirmDelete) closeDeleteConfirm();
    } catch (error) {
      console.error('Ошибка деактивации товара:', error);
      toast.error('Не удалось деактивировать товар');
    }
  };
  
  // Обработка активации товара
  const handleActivate = async (id) => {
    try {
      // Находим товар в списке
      const product = products.find(p => p.id === id);
      if (!product) {
        toast.error('Товар не найден');
        return;
      }
      
      // Активируем товар, передавая все его текущие данные
      await activateProduct(id, {
        name: product.name,
        description: product.description || '',
        barcode: product.barcode || '',
        sku: product.sku || '',
        categoryId: product.categoryId,
        manufacturerId: product.manufacturerId,
        weight: product.weight,
        volume: product.volume,
        dimensions: product.dimensions || ''
      });
      
      fetchProducts(page, pageSize);
      toast.success('Товар успешно активирован');
    } catch (error) {
      console.error('Ошибка активации товара:', error);
      toast.error('Не удалось активировать товар');
    }
  };
  
  // Получение названия категории по ID
  const getCategoryName = (categoryId) => {
    if (!categoryId) return '-';
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Неизвестная категория';
  };
  
  // Получение названия производителя по ID
  const getManufacturerName = (manufacturerId) => {
    if (!manufacturerId) return '-';
    const manufacturer = manufacturers.find(m => m.id === manufacturerId);
    return manufacturer ? manufacturer.name : 'Неизвестный производитель';
  };

  // Обработка изменения размера страницы
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setPageSize(newSize);
    setPage(1); // Сбрасываем на первую страницу при изменении размера
  };

  return (
    <div>
      <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Товары</h1>
          <p className="text-gray-600">Управление товарами системы</p>
        </div>
        
        <div className="mt-4 md:mt-0">
          <button
            onClick={handleCreateProduct}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md flex items-center"
          >
            {getIcon('plus')}
            <span className="ml-2">Добавить товар</span>
          </button>
        </div>
      </header>
      
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {getIcon('search')}
                </div>
                <input
                  type="text"
                  placeholder="Поиск по названию, штрихкоду..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex-1 md:max-w-xs">
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">Все категории</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            
            <div className="flex-1 md:max-w-xs">
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={manufacturerFilter}
                onChange={(e) => setManufacturerFilter(e.target.value)}
              >
                <option value="">Все производители</option>
                {manufacturers.map((manufacturer) => (
                  <option key={manufacturer.id} value={manufacturer.id}>{manufacturer.name}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Показывать неактивные</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Название
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Штрихкод
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Категория
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Производитель
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Вес/Объем
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Статус
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.length > 0 ? (
                  products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                        {product.description && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">
                            {product.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{product.barcode || '-'}</div>
                        {product.sku && (
                          <div className="text-xs text-gray-400">
                            Артикул: {product.sku}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{getCategoryName(product.categoryId)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{getManufacturerName(product.manufacturerId)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {product.weight && product.weightUnit ? (
                            <div>{product.weight} {getWeightUnitDisplayName(product.weightUnit)}</div>
                          ) : product.weight ? (
                            <div>{product.weight}</div>
                          ) : null}
                          {product.volume && product.volumeUnit ? (
                            <div className="text-xs text-gray-400">{product.volume} {getVolumeUnitDisplayName(product.volumeUnit)}</div>
                          ) : product.volume ? (
                            <div className="text-xs text-gray-400">{product.volume}</div>
                          ) : null}
                          {!product.weight && !product.volume && <span className="text-gray-400">-</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            product.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {product.isActive ? 'Активен' : 'Неактивен'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewProduct(product.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Просмотр"
                          >
                            {getIcon('view')}
                          </button>
                          <button
                            onClick={() => handleEditProduct(product.id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Редактировать"
                          >
                            {getIcon('edit')}
                          </button>
                          {product.isActive ? (
                            <button
                              onClick={() => openDeleteConfirm(product.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Деактивировать"
                            >
                              {getIcon('block')}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(product.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Активировать"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm font-medium text-gray-500">
                      Товары не найдены. Добавьте первый товар!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-200">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <span className="text-sm text-gray-700 mr-2">Элементов на странице:</span>
              <select
                className="border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={pageSize}
                onChange={handlePageSizeChange}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <span className="ml-4 text-sm text-gray-500">
                Показано {products.length} из {totalItems} элементов
              </span>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => page > 1 && setPage(page - 1)}
                disabled={page === 1}
                className={`px-3 py-1 rounded-md ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
              >
                Назад
              </button>
              <span className="px-3 py-1 text-gray-700">
                Страница {page} из {totalPages}
              </span>
              <button
                onClick={() => page < totalPages && setPage(page + 1)}
                disabled={page === totalPages}
                className={`px-3 py-1 rounded-md ${page === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
              >
                Далее
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Диалог подтверждения удаления */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4">Подтверждение деактивации</h3>
            <p className="mb-6">Вы уверены, что хотите деактивировать этот товар? Это может повлиять на связанные данные.</p>
            <div className="flex justify-end">
              <button
                onClick={closeDeleteConfirm}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
              >
                Отмена
              </button>
              <button
                onClick={() => handleDeactivate(confirmDelete)}
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
              >
                Деактивировать
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;